import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import {
  ScreeningCaseCompositeEntity,
  ScreeningCaseReviewStatusKey,
  ScreeningEntityScreeningCaseCompositeEntity,
} from '@nk/data-entities-lib';
import {
  CardListItemDateComponent,
  CardListItemLinkComponent,
  CardListItemTextComponent,
  createCardListConfig,
  createCardListItemConfig,
  NkCardListItemLink,
} from '@nk/ui-lib';
import { titleCaseCompanyName } from '@nk/utils-lib';
import { getLatestScreeningCaseCompositeEntity } from '../../utils/screening-case-review.utils';
import { getLatestScreeningCaseReviewCompositeEntity } from '../../utils/screening-case.utils';
import { getScreeningStatusTags, ScreeningStatusTag } from '../../utils/screening-overview.utils';
import { ScreeningCaseCardActionListItemComponent } from '../screening-case-card-action-list-item/screening-case-card-action-list-item.component';
import { ScreeningCaseCardStatusListItemComponent } from '../screening-case-card-status-list-item/screening-case-card-status-list-item.component';
import { ScreeningCaseCardListDetailsComponent } from './screening-case-card-list-details/screening-case-card-list-details.component';

export interface ScreeningCaseCardListItem {
  uuid: string;
  entity: NkCardListItemLink;
  screeningStatusTags: ScreeningStatusTag[];
  date: Date | null;
  completedReviewsCount: string;
  screeningCaseComposite: ScreeningCaseCompositeEntity | null;
  data: ScreeningEntityScreeningCaseCompositeEntity;
}

export const SCREENING_CASE_CARD_LIST_CONFIG = createCardListConfig<ScreeningCaseCardListItem>({
  items: [
    createCardListItemConfig<ScreeningCaseCardListItem, typeof CardListItemLinkComponent>({
      andClass: 'col-sm-4',
      label: extractTranslation('screening.screening_overrides.screening_case_table.table.entity_name'),
      prop: 'entity',
      component: CardListItemLinkComponent,
      sortable: true,
    }),
    createCardListItemConfig<ScreeningCaseCardListItem, typeof ScreeningCaseCardStatusListItemComponent>({
      andClass: 'col-sm-2',
      label: extractTranslation('screening.screening_overrides.screening_case_table.table.status'),
      prop: 'screeningStatusTags',
      component: ScreeningCaseCardStatusListItemComponent,
      sortable: false,
    }),
    createCardListItemConfig<ScreeningCaseCardListItem, typeof CardListItemTextComponent>({
      andClass: 'col-sm-2',
      label: extractTranslation('screening.case.review.progress_label'),
      prop: 'completedReviewsCount',
      component: CardListItemTextComponent,
      sortable: true,
    }),
    createCardListItemConfig<ScreeningCaseCardListItem, typeof CardListItemDateComponent>({
      andClass: 'col-sm-2',
      label: extractTranslation('screening.screening_overrides.screening_case_table.table.date'),
      prop: 'date',
      component: CardListItemDateComponent,
      sortable: true,
    }),
    createCardListItemConfig<ScreeningCaseCardListItem, typeof ScreeningCaseCardActionListItemComponent>({
      andClass: 'col-sm-2 d-inline-flex align-items-center justify-content-end',
      label: '',
      prop: 'screeningCaseComposite',
      component: ScreeningCaseCardActionListItemComponent,
      sortable: false,
    }),
  ],
  detailsComponent: ScreeningCaseCardListDetailsComponent,
  noDataMessage: extractTranslation('screening.screening_overrides.screening_case_table.table.no_data_found'),
});

export function transformToScreeningCaseCardListItems(
  composites: ScreeningEntityScreeningCaseCompositeEntity[],
  translateService: TranslateService,
): ScreeningCaseCardListItem[] {
  return composites
    .filter((composite) => composite.screening_case_composites?.length)
    .map((composite) => {
      let entityLink: NkCardListItemLink;

      // Determine entity type and create appropriate link
      if (composite.person) {
        entityLink = {
          routerLink: [`/person/${composite.person.uuid}/screening`],
          label: composite.person.full_name,
        };
      } else if (composite.customer_person) {
        entityLink = {
          routerLink: [`/screening/customer-person/${composite.customer_person.uuid}`],
          label: composite.customer_person.full_name,
        };
      } else if (composite.company) {
        entityLink = {
          routerLink: [`/company/${composite.company.uuid}`],
          label: titleCaseCompanyName(composite.company.name) || composite.company.name,
        };
      } else {
        entityLink = {
          routerLink: null,
          label: '-',
        };
      }

      const newestScreeningCaseComposite = getLatestScreeningCaseCompositeEntity(composite.screening_case_composites);

      const date = newestScreeningCaseComposite?.screening_entity_screening_case_state
        ?.screening_case_state_update_timestamp
        ? newestScreeningCaseComposite.screening_entity_screening_case_state.screening_case_state_update_timestamp
        : null;

      const screeningStatusTags = newestScreeningCaseComposite?.screening_entity_screening_case_state
        ? getScreeningStatusTags(newestScreeningCaseComposite.screening_entity_screening_case_state)
        : [];

      // Calculate finished review count
      const completedReviewCount = composite.screening_case_composites.filter((caseComposite) => {
        const latestReview = getLatestScreeningCaseReviewCompositeEntity(
          caseComposite.screening_case_review_composites,
        );
        if (!latestReview) {
          return false;
        }
        return (
          latestReview.screening_case_review_status.screening_case_review_status_key ===
          ScreeningCaseReviewStatusKey.complete
        );
      }).length;

      const completedReviewsCountText = translateService.instant(
        extractTranslation('screening.case.review.completed_reviews_count'),
        { count: completedReviewCount, total: composite.screening_case_composites.length },
      );

      if (!composite.customer_person && completedReviewCount === composite.screening_case_composites.length) {
        return null;
      }

      const item: ScreeningCaseCardListItem = {
        uuid: newestScreeningCaseComposite?.screening_case?.uuid || '',
        entity: entityLink,
        screeningStatusTags,
        date,
        completedReviewsCount: completedReviewsCountText,
        screeningCaseComposite: newestScreeningCaseComposite,
        data: composite,
      };

      return item;
    })
    .filter((item): item is ScreeningCaseCardListItem => item !== null);
}

export function hasNewUnhandledCases<T extends { screening_case_composites: ScreeningCaseCompositeEntity[] }>(
  elements: T[],
): boolean {
  const screeningCaseComposites = elements.reduce((res: ScreeningCaseCompositeEntity[], elem) => {
    return res.concat(elem.screening_case_composites);
  }, []);

  for (const composite of screeningCaseComposites) {
    const review = getLatestScreeningCaseReviewCompositeEntity(composite?.screening_case_review_composites);
    if (composite?.screening_case && !review) {
      return true;
    }
    if (
      composite &&
      review &&
      review.screening_case_review_status.screening_case_review_status_key !== ScreeningCaseReviewStatusKey.complete
    ) {
      return true;
    }
  }
  return false;
}
